import { uploadFile } from "../../../store/auth/actions";
import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import AudioLibraryModal from "../../../components/Modals/AudioLibraryPopup/index";
import { toast } from "react-toastify";

const Step1 = ({
  step,
  setStep,
  errors,
  setErrors,
  formData,
  handleInputChange,
}) => {
  const dispatch = useDispatch();
  const [imagePreview, setImagePreview] = useState(null);
  const [voicePreview, setVoicePreview] = useState(null);
  const [selectedVoiceName, setSelectedVoiceName] = useState(null); // Store selected voice name for display
  const [loading, setLoading] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudioLibrary, setShowAudioLibrary] = useState(false);
  const audioRef = useRef(null);

  const handleFileUpload = async (e, fieldName, setPreview, setLoading) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE_MB = fieldName === "voiceUrl" ? 25 : 10;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    // Check file size
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(
        `File size exceeds ${MAX_SIZE_MB}MB. Please upload a smaller file.`
      );
      return;
    }

    // Set local preview (temporary blob)
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    // Start loader
    setLoading(true);

    const callback = (err, res) => {
      setLoading(false); // Stop loader

      if (err) {
        alert("Something went wrong on file upload!");
      } else {
        const uploadedUrl = res?.data?.url;
        if (uploadedUrl) {
          handleInputChange(fieldName, uploadedUrl);
          // If it's voice upload, clear the voice_id since it's user uploaded
          if (fieldName === "voiceUrl") {
            handleInputChange("voiceId", "");
            setSelectedVoiceName(null);
          }
        }
      }
    };

    // Start file upload
    dispatch(uploadFile({ data: file, callback }));
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview(null);
    handleInputChange("imageUrl", "");
  };

  const handleRemoveAudio = (e) => {
    e.stopPropagation();
    setVoicePreview(null);
    setSelectedVoiceName(null);
    handleInputChange("voiceUrl", "");
    handleInputChange("voiceId", ""); // Clear voice_id as well
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handlePlayPause = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Reset audio to beginning and play
      audioRef.current.currentTime = 0;
      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error("Error playing audio:", error);
            alert("Error playing audio. Please try again.");
          });
      }
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  const handleOpenAudioLibrary = (e) => {
    e.stopPropagation();

    // Check if gender is selected
    if (!formData.gender) {
      alert("Please select gender first before choosing from audio library");
      return;
    }

    setShowAudioLibrary(true);
  };

  const handleSelectFromLibrary = (audioUrl, audioName, voiceId) => {
    setVoicePreview(audioUrl);
    setSelectedVoiceName(audioName);
    handleInputChange("voiceUrl", audioUrl);
    handleInputChange("voiceId", voiceId); // Store voice_id in form data
    setShowAudioLibrary(false);
  };

  const validateStep1 = (data) => {
    const newErrors = {};
    if (!data.gender) newErrors.gender = "Gender is required";
    if (!data.imageUrl) newErrors.imageUrl = "Image is required";
    if (!data.voiceUrl) newErrors.voiceUrl = "Voice sample is required";
    setErrors(newErrors);
    return newErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const stepErrors = validateStep1(formData);
    if (Object.keys(stepErrors).length === 0) {
      setStep(2);
    }
  };

  return (
    <div className="Wrpper d-flex flex-column  gap-4">
      <div>
        <div className="head pb-3">
          <h5 className="m-0 fw-sbold text-[24px]">Gender</h5>
        </div>
        <div className="py-3 border-bottom  border-dashed border-gray-300">
          <ul className="list-none ps-0 mb-0 d-flex align-items-center  gap-10 ">
            <li className="d-flex align-items-center  gap-10 px-3">
              <input
                type="radio"
                id="male"
                name="gender"
                className="form-check"
                value="male"
                checked={formData.gender === "male"}
                onChange={(e) => handleInputChange("gender", e.target.value)}
              />
              <label
                htmlFor="male"
                className="form-label fw-bold m-0 text-[16px]"
              >
                Men
              </label>
            </li>
            <li className="d-flex align-items-center  gap-10 px-3">
              <input
                type="radio"
                id="female"
                name="gender"
                className="form-check"
                value="female"
                checked={formData.gender === "female"}
                onChange={(e) => handleInputChange("gender", e.target.value)}
              />
              <label
                htmlFor="female"
                className="form-label fw-bold m-0 text-[16px]"
              >
                Women
              </label>
            </li>
          </ul>
          {errors.gender && (
            <div className="text-danger text-sm mt-1">{errors.gender}</div>
          )}
        </div>
      </div>

      <div>
        <div className="head pb-3">
          <h5 className="m-0 fw-sbold text-[24px]">
           Choose Your Streaming Style
          </h5>
        </div>
        <div className="py-3 border-bottom  border-dashed border-gray-300">
          <ul className="list-none ps-0 mb-0 d-flex align-items-center  gap-10 ">
            <li className="d-flex align-items-center  gap-10 px-3">
              <input
                type="radio"
                id="stitchTrue"
                name="stitch"
                className="form-check"
                value={true}
                checked={
                  formData.stitch === true || formData.stitch === undefined
                }
                onChange={(e) => handleInputChange("stitch", true)}
              />
              <label
                htmlFor="stitchTrue"
                className="form-label fw-bold m-0 text-[16px]"
              >
              Full-Body Streaming
              </label>
            </li>
            <li className="d-flex align-items-center  gap-10 px-3">
              <input
                type="radio"
                id="stitchFalse"
                name="stitch"
                className="form-check"
                value={false}
                checked={formData.stitch === false}
                onChange={(e) => handleInputChange("stitch", false)}
              />
              <label
                htmlFor="stitchFalse"
                className="form-label fw-bold m-0 text-[16px]"
              >
               Face Streaming
              </label>
            </li>
          </ul>
        </div>
      </div>

      <div>
        <div className="head">
          <h5 className="m-0 fw-sbold text-[24px]">Avatar Generation</h5>
          <p className="m-0 py-2">
            The user will have to verify themselves by uploading the below
            details
          </p>
        </div>
        <div className="py-5 d-flex flex-column   gap-10 ">
          <div className="py-2">
            <label htmlFor="" className="form-label m-0 pb-1 d-block fw-sbold">
              Upload photo
            </label>
            <div className="position-relative  py-5  border    rounded-2  border-dashed border-gray-300">
              <input
                type="file"
                accept="image/*"
                className="position-absolute z-[99] cursor-pointer opacity-0 top-0 left-0 h-100  w-100"
                onChange={(e) =>
                  handleFileUpload(e, "imageUrl", setImagePreview, setLoading)
                }
              />
              {loading && (
                <p className="text-blue-500 text-sm text-center">
                  Uploading...
                </p>
              )}
              <div className="text-center">
                {imagePreview ? (
                  <div className="position-relative   d-inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-[100px] h-[100px] mx-auto rounded object-fit-cover"
                      onClick={(e) => e.stopPropagation()}
                      style={{ height: 100, width: 100 }}
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="position-absolute -top-2 btn p-1 -right-2  bg-danger hover:bg-red-600 text-white  rounded-circle  w-6 h-6 d-flex align-items-center  justify-content-center  text-xs transition-colors"
                      type="button"
                      style={{ top: -10, right: -10, height: 20 }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="">
                    <div className="d-flex align-items-center  justify-content-center">
                      {fileIcn}
                    </div>
                    <p className="m-0 py-1">Click to Upload or drag and drop</p>
                    <p className="m-0 text-xs"> (Max. Image size: 10 MB)</p>
                  </div>
                )}
              </div>
            </div>
            {errors.imageUrl && (
              <div className="text-danger text-sm mt-1">{errors.imageUrl}</div>
            )}
          </div>

          <div className="py-2">
            <label htmlFor="" className="form-label m-0 pb-1 d-block fw-sbold">
              Upload Voice sample for voice cloning
            </label>

            {voicePreview ? (
              <>
                {/* // Audio Preview Section (Outside of file input area) */}
                {/* <div className="py-3   border    rounded-2  border-solid border-blue-200 bg-blue-50"> */}
                {voiceLoading && (
                  <p className="text-blue-500 text-sm text-center mb-3">
                    Uploading...
                  </p>
                )}
                {/* </div> */}
                <div className="d-flex flex-column  align-items-center  gap-10 border rounded-3 px-3 py-3">
                  <div className="d-flex align-items-center justify-content-between w-100  gap-10  position-relative ">
                    <div className="left d-flex align-items-center gap-10">
                      <button
                        onClick={handlePlayPause}
                        className="bg-blue-500 hover:bg-blue-600 btn bg-primary text-white rounded-pill d-flex align-items-center justify-content-center transition-colors"
                        style={{ height: 40, width: 40 }}
                        type="button"
                      >
                        {isPlaying ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <rect
                              x="2"
                              y="1"
                              width="2"
                              height="10"
                              fill="currentColor"
                            />
                            <rect
                              x="8"
                              y="1"
                              width="2"
                              height="10"
                              fill="currentColor"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path d="M2 1L10 6L2 11V1Z" fill="currentColor" />
                          </svg>
                        )}
                      </button>
                      <span className="text-sm text-gray-700 fw-sbold">
                        {isPlaying ? "Playing..." : "Click to play"}
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveAudio}
                      className=" -top-2 btn p-1 -right-2  bg-danger hover:bg-red-600 text-white  rounded-circle  w-6 h-6 d-flex align-items-center  justify-content-center  text-xs transition-colors"
                      type="button"
                      style={{
                        height: 40,
                        width: 40,
                        top: -10,
                        right: -10,
                        fontSize: 40,
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {/* <p className="text-xs text-gray-600">
                    {selectedVoiceName
                      ? `Selected: ${selectedVoiceName}`
                      : "Audio uploaded successfully"}
                  </p> */}
                  <audio
                    ref={audioRef}
                    src={voicePreview}
                    onEnded={handleAudioEnd}
                    className="hidden"
                  />
                </div>
              </>
            ) : (
              // File Upload Section
              <div className="position-relative  py-5  border    rounded-2  border-dashed border-gray-300">
                <input
                  type="file"
                  accept="audio/*"
                  className="position-absolute z-[99] cursor-pointer opacity-0 top-0 left-0 h-100  w-100"
                  onChange={(e) =>
                    handleFileUpload(
                      e,
                      "voiceUrl",
                      setVoicePreview,
                      setVoiceLoading
                    )
                  }
                />
                {voiceLoading && (
                  <p className="text-blue-500 text-sm text-center">
                    Uploading...
                  </p>
                )}
                <div className="text-center">
                  <div className="d-flex align-items-center  justify-content-center">
                    {fileIcn}
                  </div>
                  <p className="m-0 py-1">Click to Upload or drag and drop</p>
                  <p className="m-0 text-xs"> (Max. File size: 25 MB)</p>
                </div>
              </div>
            )}

            {/* Audio Library Button */}
            <div className="mt-3 text-center">
              <button
                onClick={handleOpenAudioLibrary}
                className="text-blue-500 hover:text-blue-600 commonBtn btn text-white rounded-pill fw-sbold text-sm underline transition-colors"
                type="button"
              >
                Or select from audio library
              </button>
            </div>

            {errors.voiceUrl && (
              <div className="text-danger text-sm mt-1">{errors.voiceUrl}</div>
            )}
          </div>
        </div>
      </div>

                  <div className="mt-10 mx-auto w-100" style={{ maxWidth: 300 }}>

        <button
          onClick={handleNext}
          className="d-flex align-items-center  justify-content-center  bg-blue-600 hover:bg-blue-700 text-white h-[40px] commonBtn btn rounded-pill  w-full max-w-[377px] mx-auto transition-colors w-100"
        >
          Next
        </button>
      </div>

      {/* Audio Library Modal */}

      <AudioLibraryModal
        showAudioLibrary={showAudioLibrary}
        setShowAudioLibrary={setShowAudioLibrary}
        onClose={() => setShowAudioLibrary(false)}
        onSelect={handleSelectFromLibrary}
      />
    </div>
  );
};

export default Step1;

const fileIcn = (
  <svg
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="44" height="44" rx="22" fill="#4A2CE5" />
    <path
      d="M18.9995 27.7505C18.5895 27.7505 18.2495 27.4105 18.2495 27.0005V22.8105L17.5295 23.5305C17.2395 23.8205 16.7595 23.8205 16.4695 23.5305C16.1795 23.2405 16.1795 22.7605 16.4695 22.4705L18.4695 20.4705C18.6795 20.2605 19.0095 20.1905 19.2895 20.3105C19.5695 20.4205 19.7495 20.7005 19.7495 21.0005V27.0005C19.7495 27.4105 19.4095 27.7505 18.9995 27.7505Z"
      fill="white"
    />
    <path
      d="M20.9995 23.7495C20.8095 23.7495 20.6195 23.6795 20.4695 23.5295L18.4695 21.5295C18.1795 21.2395 18.1795 20.7595 18.4695 20.4695C18.7595 20.1795 19.2395 20.1795 19.5295 20.4695L21.5295 22.4695C21.8195 22.7595 21.8195 23.2395 21.5295 23.5295C21.3795 23.6795 21.1895 23.7495 20.9995 23.7495Z"
      fill="white"
    />
    <path
      d="M25 32.75H19C13.57 32.75 11.25 30.43 11.25 25V19C11.25 13.57 13.57 11.25 19 11.25H24C24.41 11.25 24.75 11.59 24.75 12C24.75 12.41 24.41 12.75 24 12.75H19C14.39 12.75 12.75 14.39 12.75 19V25C12.75 29.61 14.39 31.25 19 31.25H25C29.61 31.25 31.25 29.61 31.25 25V20C31.25 19.59 31.59 19.25 32 19.25C32.41 19.25 32.75 19.59 32.75 20V25C32.75 30.43 30.43 32.75 25 32.75Z"
      fill="white"
    />
    <path
      d="M32 20.7505H28C24.58 20.7505 23.25 19.4205 23.25 16.0005V12.0005C23.25 11.7005 23.43 11.4205 23.71 11.3105C23.99 11.1905 24.31 11.2605 24.53 11.4705L32.53 19.4705C32.74 19.6805 32.81 20.0105 32.69 20.2905C32.57 20.5705 32.3 20.7505 32 20.7505ZM24.75 13.8105V16.0005C24.75 18.5805 25.42 19.2505 28 19.2505H30.19L24.75 13.8105Z"
      fill="white"
    />
  </svg>
);
